import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { colors } from '@guryou/colors';
import { PricingModel } from '@guryou/core';
import { DefaultText, Form, Spinner } from '@guryou/html-components';
import { getPublicSessions, stateIsNotReady } from '@guryou/redux';
import { printDate, printPrice, printTime } from '@guryou/utils';
import { WidgetContext } from './WidgetContext';

const WidgetClassSessions = ({ gyClass }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const sessions = gyClass.sessions;
  const { selectedSessions, setSessionActiveStep, setSelectedSessions, setSelsectedOfferForCourse, fontF, primaryColor } = useContext(WidgetContext);

  // Use custom primary color if available, otherwise fallback to default mandy color
  const primary = primaryColor || 'var(--feel-primary)';
  const secondary = primaryColor || colors.cabaret;
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(gyClass.model === PricingModel.total);

  useEffect(() => {
    dispatch(getPublicSessions(gyClass.id));
  }, [dispatch, gyClass.id]);

  if (stateIsNotReady(sessions)) {
    return <Spinner />;
  }

  if (!sessions.length) {
    return <DefaultText>{t('SESSIONS.noSessionsInClass')}</DefaultText>;
  }

  const selectedUniqueSessions = (id, checked) => {
    let newSelectedSessions;
    if (checked) {
      newSelectedSessions = [...selectedSessions, id];
    } else {
      newSelectedSessions = selectedSessions.filter(x => x !== id);
    }
    setSelectedSessions(newSelectedSessions);

    // Update isSelectAllChecked based on whether all sessions are selected
    const allSessionsSelected = newSelectedSessions.length === sessions.length;
    setIsSelectAllChecked(allSessionsSelected);
  };

  // Check if at least one session is selected
  const isSessionSelected = () => {
    if (gyClass.model === PricingModel.total) {
      return true; // For total pricing, all sessions are automatically selected
    }
    return isSelectAllChecked || (selectedSessions && selectedSessions.length > 0);
  };

  const onSubmit = data => {
    if (!data.sessions) {
      return;
    }

    if (gyClass.model === PricingModel.total) {
      setSessionActiveStep(s => s + 1);
      setSelectedSessions(sessions.map(x => +x.id));
      setSelsectedOfferForCourse(sessions[0].classId);
      return;
    }
    const sessionsArray = Array.isArray(data.sessions) ? data.sessions : [data.sessions];
    if (!sessionsArray.length) {
      return;
    }
    setSessionActiveStep(s => s + 1);
    setSelectedSessions(sessionsArray.map(x => +x));
    setSelsectedOfferForCourse(sessions[0].classId);
  };

  return (
    <div>
      {/* Section Selection Title */}
      <div className="mb-6">
        <h3 className={`text-lg font-semibold text-[${colors.mineShaft}] mb-1`}>{t('SESSIONS.selectSessions')}</h3>
        <p className={`text-sm text-[${colors.boulder}]`}>
          {gyClass.model === PricingModel.total ? t('SESSIONS.selectAllSessionsDescription') : t('SESSIONS.selectIndividualSessionsDescription')}
        </p>
      </div>

      <Form actions={false} parserVersion={2} onSubmit={onSubmit}>
        <div className="relative overflow-x-auto shadow-lg rounded-xl border border-gray-200 max-h-[400px]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-white uppercase" style={{ background: `linear-gradient(to right, ${primary}, ${secondary})` }}>
              <tr>
                <th scope="col" className="p-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className={`w-4 h-4 text-white bg-white/20 border-white/70 rounded checked:bg-white/30 checked:border-white focus:outline-none focus:ring-0`}
                      disabled={gyClass.model === PricingModel.total}
                      checked={isSelectAllChecked}
                      onChange={checked => {
                        const isChecked = checked.target.checked;
                        setIsSelectAllChecked(isChecked);
                        if (isChecked) {
                          // Select all sessions
                          setSelectedSessions(sessions.map(x => x.id));
                        } else {
                          // Deselect all sessions
                          setSelectedSessions([]);
                        }
                      }}
                    />
                    <label htmlFor="checkbox-all-search" className="sr-only">
                      checkbox
                    </label>
                  </div>
                </th>
                <th scope="col" className="px-6 py-4 font-semibold">
                  {`${t('GENERAL.allSessions')} ${gyClass.model === PricingModel.total ? printPrice(gyClass.price) : ''}`}
                </th>
                <th scope="col" className="px-6 py-4 font-semibold">
                  {t('SERVICES.startTime')}
                </th>
                {gyClass.model !== PricingModel.total && (
                  <th scope="col" className="px-6 py-4 font-semibold">
                    {t('GENERAL.price')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.map((x, index) => {
                return (
                  <tr key={x.id} className={`hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="w-4 p-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          style={{
                            accentColor: primary,
                          }}
                          className="w-4 h-4 bg-white border-gray-300 rounded focus:ring-2"
                          key={`${x.id}_${isSelectAllChecked || selectedSessions?.includes(+x.id)}`}
                          disabled={gyClass.model === PricingModel.total}
                          name="sessions"
                          value={x.id}
                          sx={{ fontFamily: fontF }}
                          checked={isSelectAllChecked || selectedSessions?.includes(x.id)}
                          id="checkbox1"
                          onChange={e => selectedUniqueSessions(x.id, e.target.checked)}
                        />
                        <label htmlFor="checkbox-table-search-1" className="sr-only">
                          checkbox
                        </label>
                      </div>
                    </td>
                    <th scope="row" className={`px-6 py-4 font-medium text-[${colors.mineShaft}] whitespace-nowrap`}>
                      {printDate(x.startTime)}
                    </th>
                    <td className={`px-6 py-4 text-[${colors.boulder}]`}>
                      {printTime(x.startTime)} - {printTime(x.endTime)}
                    </td>
                    {gyClass.model !== PricingModel.total && (
                      <td className="px-6 py-4 font-semibold" style={{ color: primary }}>
                        {printPrice(x.price)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-6">
          <button
            type="submit"
            disabled={!isSessionSelected()}
            style={{
              background: isSessionSelected() ? `linear-gradient(to right, ${primary}, ${secondary})` : undefined,
            }}
            className={`max-w-[180px] font-semibold rounded-xl text-sm px-6 py-3 inline-flex items-center justify-center w-full text-center transition-all duration-300 shadow-lg ${
              isSessionSelected()
                ? `text-white hover:shadow-xl hover:scale-105 focus:ring-4 focus:outline-none`
                : `text-gray-400 bg-gray-200 cursor-not-allowed shadow-none`
            }`}
            onMouseEnter={e => {
              if (isSessionSelected()) {
                e.currentTarget.style.background = `linear-gradient(to right, ${secondary}, ${primary})`;
                const color = primary.replace('#', '');
                e.currentTarget.style.boxShadow = `0 20px 25px -5px rgba(${parseInt(color.substring(0, 2), 16)}, ${parseInt(
                  color.substring(2, 4),
                  16
                )}, ${parseInt(color.substring(4, 6), 16)}, 0.25)`;
              }
            }}
            onMouseLeave={e => {
              if (isSessionSelected()) {
                e.currentTarget.style.background = `linear-gradient(to right, ${primary}, ${secondary})`;
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <span className="mr-2">{t('VIDEO_COURSE_PAGE.next')}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </Form>
    </div>
  );
};

export default WidgetClassSessions;
